import React, { useEffect, useContext, useMemo, useCallback } from 'react';
import cookies from 'js-cookie';

// BWA stuff
import useWarningOnUnload from '../../utils/useWarningOnUnload';
import logToDataDog from '../../../config/logToDataDog';
import { PatientInfoFormContext } from '../../components/PatientInfoForm/PatientInfoFormProvider';
import SignUpPageView from './SignUpPageView';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

const SignUpPageContainer = ({
    goBackToPatientInfo,
    onSignInSuccess,
    shouldShowLoginModal,
    signedIn,
    sendMetric,
    openLoginModal,
    ...viewOnlyProps
}) => {
    // Trigger the popup saying input will be lost when the user refreshes the page
    useWarningOnUnload();

    // Metrics that should only fire once
    useEffect(() => {
        // Datadog metric for prelaunch
        logToDataDog({
            name: 'booking-web-app.saw_sign_up',
            type: 'increment',
            value: 1,
        });

        /* Scroll back to top on page load; if the user scrolled
         * down to complete the form on the previous page, we don't
         * want to start them off at that random scroll point here.
         */
        window.scrollTo(0, 0);
        sendMetric({
            action: 'Loaded sign up view',
            initiator: EVENT_INITIATORS.SYSTEM,
            interactionType: INTERACTION_TYPES.LOAD,
        });
    }, [sendMetric]);

    const onClickLoginLink = useCallback(() => {
        sendMetric({
            action: 'Click email already in use error login link',
            component: 'Email already in use error',
            element: 'Login link',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        openLoginModal();
    }, [openLoginModal, sendMetric]);

    const patientInfoContext = useContext(PatientInfoFormContext);
    const skipPatientInfoForTesting = cookies.get('skipPatientInfoForTesting');

    const patientInfoHasValue = useMemo(() => {
        // Make sure all fields have non-empty values
        return Object.entries(patientInfoContext.values)
            .map(entry => {
                // eslint-disable-next-line no-unused-vars
                const [_, value] = entry;
                return value;
            })
            .every(Boolean);
    }, [patientInfoContext.values]);

    useEffect(() => {
        if (!patientInfoHasValue && !skipPatientInfoForTesting) {
            goBackToPatientInfo();
        }
    }, [goBackToPatientInfo, patientInfoHasValue, skipPatientInfoForTesting]);

    useEffect(() => {
        if (signedIn) {
            onSignInSuccess();
        }
    }, [onSignInSuccess, signedIn]);

    const viewProps = {
        onSignInSuccess,
        shouldShowLoginModal,
        goBackToPatientInfo,
        sendMetric,
        onClickLoginLink,
        ...viewOnlyProps,
    };

    return <SignUpPageView {...viewProps} />;
};

export default SignUpPageContainer;
